import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useViewPermission } from "@/context/AuthContext"
import { cn } from "@/lib/utils"
import React, { forwardRef } from "react"
import { Controller, useFormContext, useFormState } from "react-hook-form"
import { useThruFormContext } from "../../context/ThruFormContext"
import SkeletonFormControl from "../skeleton-form-control/SkeletonFormControl"

export interface ISelectInput extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string
  textArea?: boolean
  defaultValue?: string | number
  readOnly?: boolean
  required?: boolean
  children?: React.ReactNode
  loadingOverride?: boolean
  style?: any
  value?: any
  data?: any[]
  triggerClassNames?: string
}

const SelectInput: React.FC<ISelectInput> = ({
  name,
  defaultValue,
  readOnly,
  required = false,
  children,
  loadingOverride,
  style,
  value,
  data,
  triggerClassNames,
  ...rest
}: ISelectInput) => {
  const { loading: contextLoading, idPrefix, requiredPermissionsForWrite } = useThruFormContext()
  const viewAllowed = useViewPermission(requiredPermissionsForWrite)

  const loading = loadingOverride ?? contextLoading
  const id = `${idPrefix ?? ""}${name}`
  const { control } = useFormContext()
  const { errors } = useFormState({ name: name })
  const error = errors[name]

  const { dir, ...selectProps } = rest
  const props = {
    ...selectProps,
    id: id,
    type: "text",
    ...(viewAllowed && !!readOnly ? { readOnly: true, disabled: true } : {}),
    "data-toggle": "tooltip",
    style: style,
    value: value,
  }

  const ForwardedSelect = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof Select>>(
    ({ ...props }, ref) => <Select {...props} />,
  )
  ForwardedSelect.displayName = "ForwardedSelect"

  return (
    <SkeletonFormControl loading={loading ?? false} inline>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field }) => {
          return (
            <ForwardedSelect
              onValueChange={(val) => {
                field.onChange(val)
              }}
              {...props}
              {...field}
              value={field.value === null ? "" : field.value}
            >
              <SelectTrigger id={`${props.id}-trigger`} className={cn(triggerClassNames, error && "border-red-500")}>
                <SelectValue placeholder={props.placeholder} />
              </SelectTrigger>
              <SelectContent className={rest.className}>
                {!!data
                  ? data.map((item) => (
                      <SelectItem
                        key={typeof item === "object" ? item.value : item}
                        value={typeof item === "object" ? item.value : item}
                        disabled={typeof item === "object" ? item.disabled : false}
                      >
                        {typeof item === "object" ? item.label : item}
                      </SelectItem>
                    ))
                  : children}
              </SelectContent>
            </ForwardedSelect>
          )
        }}
      />
      {error && (
        <small id={`error-${id}`} className="text-red-600 text-sm mt-1">
          {error.message?.toString()}
        </small>
      )}
    </SkeletonFormControl>
  )
}

export default SelectInput
